import React, { useState, useEffect } from "react";
import styles from "../../../styles/ChordQuiz.module.css";
import { CHORD_TYPES, formatChordName, ChordType } from "../../../utils/musicTheory";
import { ChordQuizOptions, ChordTypeName } from "./ChordQuiz";
import ModeSelector, { QuizMode } from "../../../components/ModeSelector";
import Tooltip from "../../../components/Tooltip";
import { getChordReference } from "../../../data/chordReferences";

interface ChordPreQuizProps {
    setQuizOptions: (options: ChordQuizOptions) => void;
    initialOptions?: ChordQuizOptions;
}

const allChords = Object.entries(CHORD_TYPES).map(([name, intervals]) => ({
    name: name as ChordTypeName,
    intervals,
}));

export default function ChordPreQuiz({ setQuizOptions, initialOptions }: ChordPreQuizProps) {
    const [selected, setSelected] = useState<ChordTypeName[]>(
        initialOptions?.availableChords?.map((c) => c.name) ?? ["MAJOR", "MINOR"]
    );
    const [mode, setMode] = useState<QuizMode>(initialOptions?.mode ?? "time");
    const [duration, setDuration] = useState<number>(initialOptions?.timeLimitSeconds ?? 60);
    const [questionLimit, setQuestionLimit] = useState<number>(initialOptions?.questionLimit ?? 10);

    const toggleChord = (chord: ChordTypeName) => {
        setSelected((prev) =>
            prev.includes(chord) ? prev.filter((c) => c !== chord) : [...prev, chord]
        );
    };

    const toggleAll = () => {
        if (selected.length === allChords.length) {
            setSelected([]);
        } else {
            setSelected(allChords.map((i) => i.name));
        }
    };

    useEffect(() => {
        setQuizOptions({
            availableChords: allChords.filter((i) => selected.includes(i.name)),
            mode,
            timeLimitSeconds: mode === "time" ? duration : undefined,
            questionLimit: mode === "questions" ? questionLimit : undefined,
        });
    }, [selected, mode, duration, questionLimit, setQuizOptions]);

    return (
        <div className={styles.preQuizWrapper}>
            <ModeSelector value={mode} onChange={setMode} />

            {mode === "time" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Duration:{" "}
                        <input
                            type="number"
                            value={duration}
                            min={10}
                            max={600}
                            onChange={(e) => {
                                setDuration(Number(e.target.value));
                            }}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            {mode === "questions" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Question limit:{" "}
                        <input
                            type="number"
                            value={questionLimit}
                            min={1}
                            max={100}
                            onChange={(e) => {
                                setQuestionLimit(Number(e.target.value));
                            }}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            <div className={styles.selectAllWrapper}>
                <div>Chords:</div>

                <button onClick={toggleAll} className={styles.preQuizButton}>
                    {selected.length === allChords.length ? "Deselect All" : "Select All"}
                </button>
            </div>

            <div className={styles.chordsWrapper}>
                {allChords.map((chord) => {
                    const ref = getChordReference(chord.name);
                    const tooltipContent = ref ? (
                        <div className={styles.chordHint}>
                            <div className={styles.chordHintLabel}>Character:</div>
                            <div className={styles.chordHintCharacter}>
                                {ref.character}
                            </div>
                            <div className={styles.chordHintDescription}>
                                {ref.soundDescription}
                            </div>
                        </div>
                    ) : null;

                    return (
                        <Tooltip
                            key={String(chord.name)}
                            content={tooltipContent}
                            position="top"
                        >
                            <label
                                className={
                                    selected.includes(chord.name)
                                        ? `${styles.chordCheckboxWrapper} ${styles.chordCheckboxWrapperChecked}`
                                        : styles.chordCheckboxWrapper
                                }
                            >
                                {formatChordName(chord.name as ChordType)}
                                <input
                                    type="checkbox"
                                    checked={selected.includes(chord.name)}
                                    onChange={() => {
                                        toggleChord(chord.name);
                                    }}
                                    className={styles.hiddenCheckbox}
                                />
                            </label>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
}
